/**
 * Klien HTTP ke `portal-api`.
 *
 * ⚠️ **Uang selalu string desimal kanonik**, tak pernah `number` — bentuk kawat dari
 * `@osbpos/contract` dipakai apa adanya, jadi tak ada lapisan konversi yang bisa menyimpang (P1).
 *
 * ### ⛔ Tak ada `tenant` di sini — dan itu perubahan yang disengaja
 * Sebelumnya setiap jalur berbentuk `/t/:tenant/...`. Itu berarti **layar** yang menentukan tenant,
 * dan layar bisa disuruh apa saja. `design/AI-SURFACE.md` R-AI-1: tenant WAJIB datang dari
 * kredensial. Sekarang ia ada di dalam token, tak terlihat dan tak bisa disentuh klien — dan
 * server-lah yang membacanya (uji `AU9` membuktikannya dari luar).
 */
import type { AssembleCatalog } from '@osbpos/domain';
import type { PrinterProfile } from '@osbpos/contract';
import type { PromoSpec } from '@osbpos/domain';

import { lupakanToken, staffSession, tokenDariAgen, tokenDariRahasia, storedToken } from './session';

const BASE = '/api';

export interface ApiError {
  readonly error_code: string;
  readonly message: string;
  /**
   * ⭐⭐ Jalan keluar menurut KATALOG galat, dikirim server (FB-TOLAK-01).
   *
   * ⛔ Opsional karena tak semua kode punya entri katalog — dan yang tak punya tetap harus
   * tampil, bukan hilang.
   */
  readonly remediation?: string;
  readonly trace_id: string;
  /**
   * ⭐ Konteks yang ditulis server bersama kode galatnya — bahan untuk menyusun dialog yang
   * berguna (mis. `heldByName` & `heldSeconds` pada `ORDER-409-IN_USE`).
   *
   * ⚠️ `unknown` per nilai, bukan `any`: isinya berbeda per kode galat, dan yang membacanya wajib
   * memeriksa bentuknya sendiri. Percaya begitu saja = layar yang meledak pada galat yang
   * kebetulan berkonteks lain.
   */
  readonly context?: Readonly<Record<string, unknown>>;
}

export class ApiFailure extends Error {
  constructor(readonly detail: ApiError) {
    super(detail.message);
    this.name = 'ApiFailure';
  }
}

async function send(path: string, token: string | undefined, init?: RequestInit) {
  return fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token === undefined ? {} : { authorization: `Bearer ${token}` }),
      /*
       * ⭐ Token STAF ikut di setiap permintaan, bukan hanya pada aksi ber-izin. Server yang
       * memutuskan mana yang menuntutnya — layar tak boleh menebak, karena tebakan yang meleset
       * berarti aksi sah ditolak dengan pesan yang membingungkan.
       */
      /* ⛔ Sesi LURING tak punya token ⇒ header-nya tak dikirim sama sekali. Header kosong akan
         ditolak server dengan galat yang membingungkan, alih-alih jatuh ke "tanpa sesi staf". */
      ...((staffSession()?.token ?? '') === '' ? {} : { 'x-staff-token': staffSession()!.token }),
      ...(init?.headers ?? {}),
    },
  });
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  let res = await send(path, storedToken(), init);

  /*
   * ⭐ Satu kali coba-ulang saat 401 — token berumur 15 menit, dan sesi kasir jauh lebih panjang
   * dari itu. Tanpa ini, layar akan mati sendiri di tengah jam sibuk setiap seperempat jam.
   * ⚠️ Tepat SEKALI: kalau token baru pun ditolak, mengulang lagi hanya menghasilkan badai
   * permintaan ke agen — dan tetap tak ada yang bisa dijual.
   */
  if (res.status === 401) {
    lupakanToken();
    /*
     * ⭐ DUA sumber, berurutan. `device-agent` adalah jalur terminal ber-server; **rahasia
     * perangkat** adalah jalur tablet, yang tak punya agen sama sekali. Sebelum ada yang kedua,
     * tablet mati total tiap tokennya habis dan satu-satunya obatnya menempel token baru lewat
     * DevTools.
     */
    const fresh = (await tokenDariAgen()) ?? (await tokenDariRahasia());
    if (fresh !== undefined) res = await send(path, fresh, init);
  }

  const text = await res.text();
  const data: unknown = text === '' ? null : JSON.parse(text);
  if (!res.ok) {
    const d = data as Partial<ApiError> | null;
    throw new ApiFailure({
      error_code: d?.error_code ?? `HTTP-${String(res.status)}-GENERIC`,
      message: d?.message ?? res.statusText,
      // trace_id dari respons ATAU header — pengguna melaporkan satu ID, operator grep sekali.
      trace_id: d?.trace_id ?? res.headers.get('x-request-id') ?? 'unknown',
      /*
       * ⛔⛔ **`context` WAJIB ikut.** Galat di sini disusun ULANG per-field, bukan diteruskan
       * apa adanya — dan itu berarti tiap bidang baru yang dikirim server hilang diam-diam sampai
       * ada yang menambahkannya di sini. Persis itu yang terjadi pada `context`: server sudah
       * mengirimkannya, tipe `ApiError` sudah memuatnya, typecheck hijau, dan dialog paksa-buka
       * tetap menampilkan "Dipegang ? sejak kurang dari semenit lalu".
       *
       * ⚠️ `?? {}` supaya pembacanya tak perlu menjaga dua bentuk (`undefined` vs kosong).
       */
      context: d?.context ?? {},
    });
  }
  tandaiDaring();
  return data as T;
}

/** Kunci penanda "terakhir benar-benar terhubung". ⚠️ Bertahan lintas muat ulang — lihat di bawah. */
const KUNCI_DARING = 'osbpos.lastOnlineAt';

/**
 * ⭐⭐⭐ **Catat bahwa perangkat ini BENAR-BENAR menjangkau server** (keputusan #4, 2026-08-25).
 *
 * ⛔ Sengaja di sini, sesudah `res.ok` — bukan di `send()`. Permintaan yang dijawab 500 atau 401
 * memang "sampai", tapi menandainya sebagai terhubung membuat portal yang RUSAK terbaca sebagai
 * portal yang sehat, dan batas jualan luring tak pernah menyala justru saat paling dibutuhkan.
 *
 * ⚠️ Disimpan di `localStorage`, bukan di memori: muat ulang halaman adalah hal pertama yang
 * dilakukan orang saat layar bermasalah, dan penanda yang tereset di situ menghapus seluruh
 * hitungan hari tepat pada saat ia mulai berarti.
 */
function tandaiDaring(): void {
  try {
    localStorage.setItem(KUNCI_DARING, new Date().toISOString());
  } catch {
    /* ⚠️ Penyimpanan penuh/diblokir bukan alasan menggagalkan permintaan yang sudah berhasil. */
  }
}

/**
 * Sudah berapa hari perangkat ini tak menjangkau server.
 *
 * ⚠️ Belum pernah tercatat ⇒ **0**, bukan tak-hingga: perangkat yang baru dipasang belum punya
 * riwayat, dan menolak penjualan di hari pertama pemasangan adalah kegagalan yang paling mahal
 * untuk dijelaskan ke pemilik.
 */
export function hariLuring(now: Date = new Date()): number {
  try {
    const v = localStorage.getItem(KUNCI_DARING);
    if (v === null) return 0;
    const t = Date.parse(v);
    if (Number.isNaN(t)) return 0;
    return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
  } catch {
    return 0;
  }
}

export interface Ready {
  readonly status: string;
  readonly pools?: { readonly livePools: number };
}
export interface Readiness {
  readonly ready: boolean;
  readonly blockers: readonly string[];
}
/**
 * Item yang sedang ditandai HABIS. ⭐ Membawa `businessDate` supaya pengingat buka sesi bisa
 * mengatakan **"sudah 3 hari"** — angka itulah yang membuat orang memutuskan alih-alih menutupnya.
 */
export interface SoldOutItem {
  /** ⭐ `product` atau `modifier` — dua sasaran, satu daftar (0067). */
  readonly kind?: 'product' | 'modifier';
  /** Untuk `modifier`: `<groupCode>/<optionCode>` — kode opsi unik per GRUP. */
  readonly productCode: string;
  readonly productName: string;
  readonly businessDate: string;
  readonly markedAt: string;
  readonly markedByCode: string;
  readonly markedByName: string | null;
}

export interface MenuItem {
  readonly code: string;
  readonly name: string;
  /** ⭐ Tiga tingkat: departemen → kategori → produk. `null` = belum diklasifikasi (TERLIHAT). */
  readonly departmentCode?: string | null;
  readonly departmentName?: string | null;
  readonly categoryCode?: string | null;
  readonly categoryName?: string | null;
  readonly price: string;
  /**
   * ⭐ Boleh kena potongan? ⚠️ Opsional & bawaan **BOLEH** — sama dengan makna
   * `product.discount_allowed IS NULL` di server, dan sama dengan katalog tiruan di uji yang belum
   * membawanya. ⛔ Yang bertanda `false` (rokok, voucher) tak ikut jadi dasar promo.
   */
  readonly discountAllowed?: boolean;
  /**
   * ⭐⭐ `combo` ⇒ layar WAJIB menanyakan pilihannya sebelum baris boleh lahir (§5c).
   *
   * ⚠️ Opsional di tipe karena katalog tiruan di uji belum membawanya; yang tak membawanya
   * diperlakukan `simple`. Server tetap menolak combo yang lewat jalur biasa
   * (`ORDER-409-STATE`) — layar cuma membuat penolakan itu tak perlu terjadi di depan tamu.
   */
  readonly productKind?: string;
}

export interface ComboOption {
  readonly productCode: string;
  readonly productName: string;
  /**
   * ⭐⭐ Berapa BANYAK yang didapat kalau opsi ini dipilih — milik OPSI, bukan grup (K26).
   *
   * "Pilih roti canai ayam dapat 2, beef dapat 1" dijawab di sini, dengan grup tetap `min=max=1`.
   * ⛔ Bukan dengan `max_select = 2` yang menyerahkan jumlahnya ke ingatan pelayan — kata Imron,
   * *"yang protes orang dapur"*.
   */
  readonly defaultQty: string;
  /** Selisih harga PAKET, bukan harga komponen — komponen selalu nol di tagihan. */
  readonly upcharge: string;
  readonly isDefault: boolean;
}

export interface ComboGroup {
  readonly comboCode: string;
  readonly groupCode: string;
  readonly groupName: string;
  /** ⛔ `> 0` = WAJIB dijawab. Server menolak lewat `validateComboSelections`. */
  readonly minSelect: number;
  readonly maxSelect: number;
  readonly options: readonly ComboOption[];
}

/** Yang dikirim ke server saat menambah satu set menu. */
export interface ComboSelectionInput {
  readonly groupCode: string;
  readonly productCode: string;
}

export interface PaymentMethod {
  readonly code: string;
  readonly name: string;
  /** ⭐ Kode, bukan nama — label diambil dari kamus `enum.tenderType.*` (design/I18N.md §3). */
  readonly tenderType: string;
  readonly requiresReference: boolean;
  readonly opensDrawer: boolean;
  /**
   * ⭐⭐ Metode ini lewat mesin gesek (EDC) ⇒ layar WAJIB meminta kode approval (K7).
   * ⚠️ Opsional di tipe karena katalog tiruan di uji belum membawanya; yang tak membawanya
   * diperlakukan BUKAN-EDC — dan server tetap menolak kalau ternyata iya.
   */
  readonly viaEdc?: boolean;
}

export interface ModifierOption {
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  /** ⚠️ Desimal kanonik, bukan `number` — selisih harga tetap uang. */
  readonly priceDelta: string;
}

export interface ModifierGroup {
  readonly productCode: string;
  readonly groupCode: string;
  readonly groupName: string;
  /** ⛔ `> 0` = WAJIB dijawab sebelum baris boleh dibuat. */
  readonly minSelect: number;
  readonly maxSelect: number;
  readonly allowFreeText: boolean;
  readonly options: readonly ModifierOption[];
}

/** Yang dikirim ke server saat menambah baris. Teks bebas SELALU tanpa harga (§5d). */
export interface ModifierSelectionInput {
  readonly groupCode: string;
  readonly optionCode?: string;
  readonly freeText?: string;
}

export interface Catalog {
  /**
   * ⭐⭐⭐ **Batas jualan luring** — setelan tenant, bawaan 3 hari (keputusan #4, 2026-08-25).
   * ⚠️ Opsional: katalog SALINAN yang tersimpan sebelum 2026-08-25 tak memuatnya, dan pembacanya
   * jatuh ke bawaan yang sama dengan DDL.
   */
  readonly offlineMaxDays?: number;
  /** Berapa lama OUTLET terputus dari HQ menurut mini-PC. Portal & tier tablet selalu 0. */
  readonly outletOfflineDays?: number;
  /**
   * ⭐⭐ **Mode jual** — menentukan BENTUK layar: `table_service` mulai dari meja, `quick_service`
   * langsung ke menu. Kolom wajib-isi di `outlet` (Guardrail #2), bukan ada-tidaknya kapabilitas.
   */
  readonly saleMode?: 'table_service' | 'quick_service';
  /** Kapabilitas lain (dapur, barcode, timbangan…). ⚠️ Mode jual TIDAK di sini. */
  readonly capabilities?: readonly string[];
  readonly modifierGroups?: readonly ModifierGroup[];
  /** Grup pilihan untuk item ber-`productKind: 'combo'` (§5c). */
  readonly comboGroups?: readonly ComboGroup[];
  /**
   * ⭐⭐⭐ Promo yang berlaku di outlet ini — supaya potongan bisa dihitung SAAT LURING (2026-08-25).
   * ⚠️ Opsional: server lama tak mengirimkannya, dan tablet yang katalognya belum diperbarui harus
   * tetap bisa jualan — hanya tanpa promo, bukan gagal.
   */
  readonly promos?: readonly PromoSpec[];
  readonly outletCode: string;
  readonly priceChannel: string;
  readonly items: readonly MenuItem[];
  readonly paymentMethods: readonly PaymentMethod[];
  /**
   * ⭐⭐⭐ Aturan pajak — ada sejak tier satu-tablet (K13) butuh menghitung SENDIRI.
   * ⚠️ Opsional di tipe karena katalog lama (dan tiruan di uji) belum membawanya; yang merakit
   * dokumen WAJIB menolak kalau ia tak ada — menghitung tanpa pajak lebih berbahaya daripada gagal.
   */
  readonly tax?: AssembleCatalog['tax'];
  /**
   * ⭐ Kode produk yang sedang HABIS. ⛔ Item-nya TETAP di `items` — yang lenyap dari kisi terbaca
   * sebagai menu rusak, dan kasir kehilangan benda yang harus ditekan untuk menyalakannya lagi.
   */
  readonly soldOut?: readonly string[];
  /** ⭐ Opsi modifier yang habis — `<groupCode>/<optionCode>` (K28 bagian modifier). */
  readonly soldOutModifiers?: readonly string[];
  /**
   * ⭐⭐⭐ Rute dapur + stasiunnya — supaya tier tablet bisa MENCETAK TIKET SENDIRI saat luring.
   * ⚠️ Opsional di tipe: katalog lama & tiruan di uji belum membawanya, dan yang tak membawanya
   * berarti "tak ada rute" — bukan galat.
   */
  readonly kitchen?: {
    readonly stations: readonly {
      code: string; name: string; kind: string; printer: unknown;
    }[];
    readonly routes: readonly { productCode: string; stationCode: string }[];
  };
  /** ⭐ Sidik jari harga+pajak. Ikut di dokumen penjualan; ia yang menjelaskan selisih kelak. */
  readonly version?: string;
}
/**
 * Jawaban `POST /orders/:id/print` — pencatatan **dan** job cetaknya.
 *
 * ⚠️ Dua bagian yang bisa gagal terpisah: `printCount` selalu naik (pencatatannya sah), `job` bisa
 * tak ada. Menyatukannya jadi "berhasil/gagal" tunggal menyembunyikan bahwa jejaknya sudah tertulis.
 */
export interface ShiftReportJobResponse {
  readonly docType: 'x_report' | 'z_report';
  readonly bytesBase64: string;
  readonly address: unknown;
  readonly requiresAgent: boolean;
  readonly doc: {
    readonly kind: 'x' | 'z';
    readonly shiftNo: string;
    readonly businessDate: string;
    readonly sales: { readonly grandTotal: string };
    readonly cash: { readonly expected: string; readonly counted: string | null };
  };
}

export interface PrintResponse {
  readonly docType: 'bill' | 'receipt';
  readonly printCount?: number;
  readonly job?: {
    readonly bytesBase64: string;
    readonly address: { transport: string; host?: string; port?: number };
    readonly requiresAgent: boolean;
  };
  readonly printError?: { readonly code: string; readonly message: string };
}

export interface Bill {
  readonly subtotalGross: string;
  readonly discountTotal: string;
  readonly serviceChargeTotal: string;
  readonly taxTotal: string;
  readonly grandTotal: string;
  readonly status: string;
}

/** §11 — panggilan tamu yang masih terbuka (layar pelayan). */
export interface GuestCall {
  readonly id: string;
  readonly tableCode: string;
  readonly areaId: string;
  readonly areaName: string;
  readonly kind: 'waiter' | 'bill';
  readonly note: string | null;
  readonly createdAt: string;
  /** ⭐ Dihitung SERVER — jam HP pelayan tak menentukan urutan mendatangi meja. */
  readonly waitingSeconds: number;
}

/** Identitas outlet untuk LAYAR — dipakai layar login & chrome kasir. */
export interface Branding {
  readonly outletCode: string;
  readonly outletName: string;
  readonly brandCode: string;
  readonly brandName: string;
  /** `null` ⇒ layar menampilkan NAMA brand dalam huruf besar, bukan kotak kosong. */
  readonly logoUrl: string | null;
}

export interface ParkedOrder {
  readonly orderId: string;
  readonly orderNo: string;
  readonly kind: 'table' | 'name' | 'queue';
  /** Tanda yang SIAP DIBACA — dirakit server, bukan di layar (supaya sama di kasir & laporan). */
  readonly label: string;
  readonly tableCode: string | null;
  readonly lineCount: number;
  /**
   * ⭐ Total **BERJALAN** (Σ harga baris) — ⛔ bukan `grandTotal`: bill tertahan menurut definisinya
   * belum ditagih, jadi pajak & service charge belum ada. Dulu bernama `grandTotal` dan membaca
   * kolom yang hanya diisi saat menagih ⇒ setiap bill tertahan tampil **Rp 0**.
   */
  readonly runningTotal: string;
  readonly parkedAt: string;
  /** Terisi bila pesanan ini sedang dipegang terminal lain. */
  readonly heldByName: string | null;
}

export interface KitchenTicketLine {
  /** ⭐ Id baris TIKET — sasaran bump per baris. Beda dari `lineId` (baris pesanan). */
  readonly id: string;
  readonly lineId: string;
  readonly name: string;
  readonly qty: string;
  readonly detail: string | null;
  /** ⛔ Kepala set menu: ditampilkan, TAK bisa diketuk; hilang sendiri saat anaknya habis. */
  readonly isHeader: boolean;
  /** Baris pesanan induk bila ini komponen paket — layar memakainya untuk indentasi. */
  readonly parentLineId: string | null;
}

/** ⚠️ `ageSeconds` datang dari SERVER — jam tablet dapur tak bisa dipercaya. */
export interface KitchenTicket {
  readonly ticketId: string;
  readonly orderId: string;
  readonly orderNo: string;
  readonly tableCode: string | null;
  /**
   * ⭐⭐ **Tanda pesanan** — `table` · `queue` · `name`. ⛔ `orderNo` tak berarti apa-apa bagi juru
   * masak: ia tak pernah menyebutnya dan tak bisa memakainya untuk mengantar. Yang ia pakai
   * "Meja 5" / "Antrean 7" / "Budi" — tanda yang sama yang diucapkan kasir dan didengar tamu.
   */
  readonly parkKind?: 'table' | 'queue' | 'name' | null;
  readonly parkLabel?: string | null;
  readonly sendRound: number;
  readonly status: 'queued' | 'in_progress' | 'done';
  readonly createdAt: string;
  readonly ageSeconds: number;
  readonly lines: readonly KitchenTicketLine[];
}

export interface QueueEntry {
  readonly orderId: string;
  readonly label: string;
  readonly ageSeconds: number;
}

export interface QueueBoard {
  readonly preparing: readonly QueueEntry[];
  readonly ready: readonly QueueEntry[];
  /** ⛔ `false` = outlet ini tak memakai tiket dapur ⇒ katakan, jangan tampilkan papan kosong. */
  readonly hasTickets: boolean;
}

/**
 * Apa yang masih bisa dikembalikan dari sebuah penjualan — dan berapa.
 * ⭐ Sisanya dijawab SERVER: kasir yang menebak sisanya akan ditolak di depan tamu yang sudah
 * dijanjikan uangnya kembali.
 */
export interface RefundableLine {
  readonly orderLineId: string;
  readonly productCode: string;
  readonly productName: string;
  readonly qtySold: string;
  readonly qtyRefunded: string;
  readonly qtyRefundable: string;
  readonly lineTotal: string;
  readonly serviceCharge: string;
  readonly taxAmount: string;
}

export interface RefundableOrder {
  readonly orderId: string;
  readonly orderNo: string;
  readonly businessDate: string;
  readonly status: string;
  readonly grandTotal: string;
  readonly refundedTotal: string;
  readonly lines: readonly RefundableLine[];
  readonly tenders: readonly {
    readonly paymentMethodCode: string | null;
    readonly tenderType: string;
    readonly amount: string;
  }[];
}

/** Satu bill di hasil pencarian. ⛔ Tak pernah agregat — yang menjumlahkan adalah laporan. */
export interface FoundOrder {
  readonly orderId: string;
  readonly orderNo: string;
  readonly businessDate: string;
  readonly status: string;
  readonly tableCode: string | null;
  readonly parkKind: string | null;
  readonly parkLabel: string | null;
  readonly guestCount: number;
  readonly grandTotal: string;
  readonly closedAt: string | null;
  readonly closedByName: string | null;
  readonly deviceCode: string | null;
  readonly tenderTypes: readonly string[];
  /**
   * ⭐⭐ Sudah pernah di-refund berapa. ⛔ Ikut di HASIL PENCARIAN, bukan cuma di rincian: kasir
   * yang membuka bill lalu baru tahu ia sudah dikembalikan kemarin sudah terlanjur menjanjikan
   * sesuatu kepada tamunya.
   */
  readonly refundedTotal: string;
}

/** Ringkasan penjualan hari ini untuk layar kasir. ⛔ Angkanya dari SERVER — layar tak menjumlahkan. */
export interface SalesToday {
  readonly businessDate: string;
  readonly bills: number;
  readonly guests: number;
  readonly grandTotal: string;
  readonly items: readonly {
    readonly productCode: string;
    readonly productName: string;
    readonly categoryName: string | null;
    readonly qtySold: string;
    readonly netSold: string;
    readonly qtyRefunded: string;
    readonly netRefunded: string;
    readonly netAfterRefund: string;
    readonly share: string;
  }[];
}

export const api = {
  /* ⛔ Tanggal usaha dari SERVER — pelayan tak punya sesi kas yang menjangkarnya. */
  /**
   * ⭐⭐⭐ **Siapa perangkat ini** — dijawab SERVER dari klaim token, bukan dari `localStorage`.
   *
   * ⛔ `shell` boleh `null`: perannya belum punya layar (kiosk, ringkasan dapur, agen cetak). Itu
   * jawaban yang SAH, bukan alasan jatuh ke kasir.
   */
  whoAmI: () =>
    req<{
      deviceCode: string;
      outletCode: string;
      role: string;
      shell: 'cashier' | 'waiter' | 'kds' | 'queue' | null;
      name: string | null;
      /**
       * ⭐⭐⭐ Tier penempatan (K13). ⚠️ Opsional di tipe karena server lama belum mengirimkannya;
       * yang tak membawanya diperlakukan `server` — perilaku yang sudah berjalan, bukan mode
       * hitung-sendiri. Tebakan yang salah arah membuat tablet menghitung bill padahal tak
       * seharusnya.
       */
      tier?: 'server' | 'tablet';
      /**
       * ⭐⭐ Profil printer perangkat ini — dipakai layar kasir untuk menendang laci SENDIRI saat
       * tender (K27, diamandemen 2026-08-25). ⚠️ Opsional: server lama tak mengirimkannya, dan
       * `null` (belum disetel) adalah jawaban yang sah.
       */
      printer?: PrinterProfile | null;
    }>('/devices/me'),

  /**
   * ⭐ Membawa **aturannya** (`timezone` + `cutoverTime`), bukan cuma jawabannya — supaya tablet
   * bisa menghitung sendiri saat portal tak terjangkau (K13). ⚠️ Keduanya opsional di tipe karena
   * server lama belum mengirimkannya; klien wajib tahan menghadapi ketiadaannya.
   */
  businessDate: (outlet: string) =>
    req<{
      businessDate: string; dayClosed: boolean; timezone?: string; cutoverTime?: string;
    }>(`/outlets/${outlet}/business-date`),
  /* ⭐ Papan antrean — tanpa sesi staf: layar ruang makan menyala tanpa penjaga. */
  queueBoard: (outlet: string, businessDate: string) =>
    req<QueueBoard>(`/outlets/${outlet}/queue?businessDate=${businessDate}`),
  /* ── layar dapur (KDS) ─────────────────────────────────────────────────── */
  /**
   * ⭐⭐ **Staf mengganti PIN-nya sendiri.** ⛔ Bukan `master:write` — yang menjaganya PIN LAMA.
   * ⚠️ `staffCode` ikut di body dan itu tak melonggarkan apa pun: tanpa PIN lama yang cocok ia
   * ditolak, jadi menyebut kode orang lain tak membeli apa-apa.
   */
  changeOwnPin: (staffCode: string, currentPin: string, newPin: string) =>
    req<{ staffCode: string; changedAt: string }>('/auth/pin/change', {
      method: 'POST', body: JSON.stringify({ staffCode, currentPin, newPin }),
    }),
  kitchenStations: (outlet: string) =>
    req<{ code: string; name: string }[]>(`/outlets/${outlet}/kitchen/stations`),
  /* ⛔ Ambang usia tiket = DATA (brand, boleh ditimpa outlet) — bukan konstanta di layar. */
  kitchenThresholds: (outlet: string) =>
    req<{ warnSeconds: number; lateSeconds: number; inherited: boolean }>(
      `/outlets/${outlet}/kitchen/thresholds`,
    ),
  kitchenTickets: (outlet: string, station: string) =>
    req<KitchenTicket[]>(`/outlets/${outlet}/kitchen/${station}/tickets`),
  /**
   * ⭐⭐⭐ **"All day"** — total per item yang MASIH harus dimasak, lintas seluruh tiket terbuka.
   *
   * ⚠️ Endpoint terpisah dari daftar tiket: layar membacanya berdampingan, dan menyatukannya
   * membuat tiap penyegaran tiket ikut menghitung ulang agregat yang jauh lebih mahal.
   */
  kitchenAllDay: (outlet: string, station: string) =>
    req<{ items: readonly {
      productCode: string; productName: string; qty: string; ticketCount: number;
      oldestSeconds: number;
    }[] }>(`/outlets/${outlet}/kitchen/${station}/all-day`),

  kitchenStart: (ticketId: string) =>
    req<{ status: string }>(`/kitchen/tickets/${ticketId}/start`, { method: 'POST' }),
  /* ⛔ Bump bisa DITOLAK (`KDS-409-ALREADY_DONE`) — pemanggilnya wajib memuat ulang, tak menelan. */
  kitchenBump: (ticketId: string) =>
    req<{ status: string; doneAt: string }>(`/kitchen/tickets/${ticketId}/bump`, { method: 'POST' }),
  /** ⭐ Satu hidangan keluar. Baris terakhir menutup tiketnya sendiri — server yang memutuskan. */
  kitchenBumpLine: (ticketLineId: string) =>
    req<{ ticketId: string; ticketDone: boolean }>(
      `/kitchen/lines/${ticketLineId}/bump`, { method: 'POST' },
    ),
  kitchenRecall: (ticketId: string) =>
    req<{ status: string }>(`/kitchen/tickets/${ticketId}/recall`, { method: 'POST' }),
  guestCalls: (outlet: string, areaId?: string) =>
    req<GuestCall[]>(
      `/outlets/${outlet}/guest-calls${areaId === undefined ? '' : `?areaId=${areaId}`}`,
    ),
  ackGuestCall: (id: string) =>
    req<{ id: string; status: string }>(`/guest-calls/${id}/ack`, { method: 'POST' }),
  /** ⭐ "Bukan tamu" — putusan MANUSIA; sistem yang menegakkannya (menangguhkan QR meja). */
  spamGuestCall: (id: string) =>
    req<{ id: string; suspendedUntil: string | null; spamCount: number }>(
      `/guest-calls/${id}/spam`,
      { method: 'POST' },
    ),
  ready: () => req<Ready>('/ready'),
  catalog: (outlet: string, channel: string, businessDate: string) =>
    req<Catalog>(
      `/outlets/${outlet}/catalog?priceChannel=${channel}&businessDate=${businessDate}`,
    ),
  readiness: (outlet: string) => req<Readiness>(`/outlets/${outlet}/readiness`),
  openOrder: (body: unknown) =>
    req<{ id: string; status: string }>('/orders', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addLine: (orderId: string, body: unknown) =>
    req<{ lineId: string; lineTotal: string }>(`/orders/${orderId}/lines`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * ⭐ Jalur TERPISAH untuk set menu — bukan `addLine` dengan bendera tambahan.
   *
   * ⛔ Server menolak combo di jalur biasa dan produk biasa di jalur ini; dua penolakan itulah
   * yang membuat "satu baris induk + N komponen" jadi satu-satunya bentuk set menu yang mungkin.
   */
  addComboLine: (orderId: string, body: unknown) =>
    req<{ lineId: string; lineTotal: string;
          components: { lineId: string; productCode: string }[] }>(
      `/orders/${orderId}/combo-lines`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  bill: (orderId: string, body: unknown) =>
    req<Bill>(`/orders/${orderId}/bill`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  addTender: (orderId: string, body: unknown) =>
    req<{ tenderId: string; remaining: string }>(`/orders/${orderId}/tenders`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  /**
   * ⭐⭐ Mencatat pencetakan **dan** menerima byte ESC/POS yang dirender SERVER.
   *
   * ⛔ Byte-nya tak pernah disusun di sini. Ia memuat `ESC p` (tendangan laci) yang lahir dari
   * `payment_method.opens_drawer` — kalau layar yang menyusunnya, "laci boleh terbuka" jadi
   * keputusan layar, dan seluruh kontrol `drawer:open` (yang bahkan menuntut PIN ulang) jadi sia-sia.
   *
   * ⚠️ `job` bisa kosong dengan `printError` terisi: pencetakan TETAP tercatat, hanya byte-nya tak
   * bisa dibuat (mis. perangkat belum punya profil printer). Layar wajib mengatakannya.
   */
  /**
   * ⭐ Tiket dapur siap cetak untuk satu pesanan — satu job per stasiun ber-printer.
   *
   * ⚠️ `failed` bukan galat permintaan: tiket yang stasiunnya belum punya printer dilaporkan di
   * situ, sementara yang lain tetap bisa dicetak. Layar boleh mengabaikannya (cetak tak boleh
   * menghalangi transaksi), tapi ia tetap dikirim supaya bisa DIKATAKAN kalau perlu.
   */
  kitchenTicketJobs: (orderId: string, round?: number) =>
    req<{
      jobs: readonly { ticketId: string; bytesBase64: string; address: unknown }[];
      failed: readonly { ticketId: string; code: string; message: string }[];
    }>(`/orders/${orderId}/kitchen-tickets/print${round === undefined ? '' : `?round=${String(round)}`}`),

  /**
   * ⭐ **X-report / Z-report** — rekap sesi kas, byte dirender SERVER (K27).
   *
   * ⚠️ `job` bisa `null` bila permintaannya datang dari sesi yang bukan perangkat terdaftar
   * (mis. laptop manajer): rekapnya tetap terbaca, cuma tak ada yang bisa dicetak. Layar wajib
   * membedakan itu dari gagal.
   */
  shiftReport: (shiftId: string, kind: 'x' | 'z') =>
    req<{ job?: ShiftReportJobResponse | null; report?: unknown }>(
      `/shifts/${shiftId}/report?kind=${kind}`,
    ),

  /**
   * ⭐⭐⭐ **Z-report tingkat TOKO** — berita acara tutup hari (2026-08-25).
   * ⚠️ `job` bisa `null`: layar yang dibuka dari laptop pemilik tetap MEMBACA angkanya walau tak
   * ada printer di sana.
   */
  storeReport: (outletCode: string, businessDate: string) =>
    req<{ job?: { bytesBase64: string; address: unknown; doc: unknown } | null; report?: unknown }>(
      `/outlets/${outletCode}/business-days/${businessDate}/report`,
    ),

  print: (orderId: string, body: unknown) =>
    req<PrintResponse>(`/orders/${orderId}/print`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  reprint: (orderId: string, body: unknown) =>
    req<PrintResponse & { reprintCount: number; priorityReview: boolean }>(
      `/orders/${orderId}/reprint`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  /**
   * ⭐ Buka laci kas tanpa transaksi. ⛔ PIN dikirim sekali untuk aksi ini saja — **tak disimpan**
   * di mana pun di layar; menyimpannya berarti PIN yang bisa dipakai ulang tanpa pemiliknya tahu.
   */
  /** ⭐ Dashboard meja: kosong · ada bill · sedang digabung. */
  tables: (outletCode: string) =>
    req<readonly {
      tableCode: string; tableKind: string; seats: number; areaName: string | null;
      orderId: string | null; orderNo: string | null; orderStatus: string | null;
      mergedUnder: string | null; mergedChildren: readonly string[];
      heldByName?: string | null; heldAt?: string | null;
    }[]>(`/outlets/${outletCode}/tables`),

  mergeTables: (outletCode: string, body: { parentTableCode: string; childTableCodes: string[] }) =>
    req<{ parent: string; merged: string[] }>(
      `/outlets/${outletCode}/tables/merge`, { method: 'POST', body: JSON.stringify(body) },
    ),

  releaseTables: (outletCode: string, body: { childTableCodes: string[] }) =>
    req<{ released: number }>(
      `/outlets/${outletCode}/tables/release`, { method: 'POST', body: JSON.stringify(body) },
    ),

  /** ⭐ Koreksi jumlah tamu — pelayan sering salah ketik saat membuka meja. */
  changeGuestCount: (orderId: string, guestCount: number) =>
    req<{ from: number; to: number }>(
      `/orders/${orderId}/guest-count`, { method: 'POST', body: JSON.stringify({ guestCount }) },
    ),

  /** ⭐ Login PIN kasir — menghasilkan token sesi staf berumur satu shift. */
  loginPin: (body: { staffCode: string; pin: string }) =>
    req<{
      staffCode: string; staffName: string; permissions: readonly string[];
      staff_token: string; expires_in: number;
    }>('/auth/pin', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * ⭐ Sesi kas yang sedang terbuka di perangkat ini. `{}` = tak ada.
   *
   * ⚠️ Dipanggil SEBELUM mencoba membuka — layar yang selalu membuka baru mengubah gerbang
   * "satu sesi per perangkat" jadi jebakan: reload halaman = kasir terkunci dari sesinya sendiri.
   */
  /** ⭐ "Apakah saya yang terakhir?" — penentu munculnya tawaran Tutup Toko sesudah sesi ditutup. */
  openShiftCount: (outlet: string, businessDate: string) =>
    req<{ today: number; total: number; devices?: readonly string[] }>(
      `/outlets/${outlet}/shifts/open-count?businessDate=${encodeURIComponent(businessDate)}`,
    ),

  currentShift: (outlet: string, deviceCode: string) =>
    req<{ shiftId?: string; businessDate?: string; shiftNo?: string; openedByName?: string }>(
      `/outlets/${outlet}/shifts/current?deviceCode=${encodeURIComponent(deviceCode)}`,
    ),

  openShift: (body: {
    outletCode: string;
    /* ⛔ Sengaja TAK ADA `shiftNo`: nomornya diterbitkan SERVER (2026-08-22). Klien yang
       mengarangnya sebagai `<perangkat>/<tanggal>` membuat sesi KEDUA di hari yang sama menabrak
       indeks unik, dan bocor ke layar sebagai "kesalahan internal". */
    businessDate: string;
    deviceCode: string; openingFloat?: string; openingNote?: string; actor: unknown;
  }) => req<{ shiftId: string; businessDate: string }>('/shifts',
    { method: 'POST', body: JSON.stringify(body) }),

  closeShift: (shiftId: string, body: unknown) =>
    req<{
      shiftId: string; expectedCash: string; countedCash: string; variance: string;
      needsApproval: boolean;
      drawerOpens: readonly { reasonCode: string; count: number }[];
    }>(`/shifts/${shiftId}/close`, { method: 'POST', body: JSON.stringify(body) }),

  closeBusinessDay: (body: unknown) =>
    req<{ shiftCount: number; openOrderCount: number; totalVariance: string }>(
      '/business-days/close', { method: 'POST', body: JSON.stringify(body) },
    ),

  openDrawer: (body: {
    shiftId: string;
    reasonCode: string;
    reasonNote?: string;
    pin: string;
    deviceCode?: string;
  }) =>
    req<{ drawerEventId: string; sequenceInShift: number; priorityReview: boolean }>(
      '/drawer/open',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  branding: (outlet: string) => req<Branding>(`/outlets/${outlet}/branding`),

  /**
   * ⭐ Mendorong satu amplop sinkron NAIK. Balasannya **selalu 200**, termasuk saat ditolak —
   * jawabannya ACK, dan ack yang ditolak tetap ack. ⇒ `req` yang melempar di sini berarti
   * kegagalan **transport**, dan itu artinya "coba lagi", bukan "buang".
   */
  /* ⭐ Tier satu-tablet: SATU penjualan utuh, idempoten lewat `orderId` dari klien (K13). */
  sendSale: (doc: unknown) =>
    req<{ orderId: string; duplicate: boolean; grandTotal: string; variance: string }>(
      '/sync/sale',
      { method: 'POST', body: JSON.stringify(doc) },
    ),

  syncUp: (envelope: unknown) =>
    req<{ batch_result: 'applied' | 'partial' | 'rejected'; batch_reason: string | null }>(
      '/sync/up',
      { method: 'POST', body: JSON.stringify(envelope) },
    ),

  /**
   * ⭐ Melepas kunci sunting MILIK SENDIRI — dipanggil saat pelayan kembali ke dashboard tanpa
   * mengirim (menahan pesanan). Tanpa ini, satu-satunya jalan membebaskan mejanya adalah
   * paksa-buka lewat kasir — dan paksa-buka yang dipakai setiap hari berhenti berarti apa-apa.
   */
  releaseHold: (orderId: string) =>
    req<{ released: boolean }>(`/orders/${orderId}/release-hold`, { method: 'POST' }),

  /**
   * ⭐⭐⭐ **PAKSA-BUKA** — merebut kunci sunting dari pemegang lain.
   *
   * ⛔ **Dua kontrol, bukan satu:** izin `order:takeover` ("harus lewat kasir") **dan** PIN
   * diminta ULANG. PIN di sini bukan autentikasi — sesinya sudah sah — melainkan **bukti
   * kehadiran**, sama seperti buka laci. Keduanya ditegakkan SERVER; layar cuma menyediakan
   * jalannya.
   *
   * ⚠️ Merebut kunci MILIK SENDIRI dari perangkat lain tak lewat sini — itu jalan biasa
   * (`takeEditHold` mengizinkan pemegang yang sama), dan memaksanya lewat kasir hanya melatih
   * semua orang memakai paksa-buka sebagai jalan biasa.
   */
  takeOver: (
    orderId: string,
    body: { pin: string; reasonCode?: string; reasonNote?: string; deviceCode?: string },
  ) =>
    req<{
      previousHolderCode: string | null;
      previousHolderName: string | null;
      heldSeconds: number | null;
    }>(`/orders/${orderId}/take-over`, { method: 'POST', body: JSON.stringify(body) }),

  /**
   * ⭐⭐ **Buka kembali bill LUNAS** — untuk kesalahan catat yang belum sempat jadi kenyataan
   * (kasir memilih QRIS padahal tamu membayar tunai, dan tamunya masih berdiri di depan kasir).
   *
   * ⛔ Layar TIDAK menebak boleh-tidaknya. Kebijakan tunai per-outlet
   * (`allowReopenAfterCashTender`), sesi kas yang masih terbuka, dan status ack ERP semuanya
   * diputuskan server — yang di sini cuma menyampaikan penolakannya dengan jujur. Menyembunyikan
   * tombolnya berdasarkan tebakan klien berarti kasir melihat pintu yang berbeda dari pintu yang
   * sebenarnya dijaga.
   */
  reopen: (orderId: string, body: { reasonCode: string; reasonNote?: string }) =>
    req<{ status: string; reversedTenders: number; grandTotal: string }>(
      `/orders/${orderId}/reopen`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * ⭐⭐ **Tahan pesanan** — menyisihkannya supaya kasir bisa melayani tamu berikutnya.
   *
   * ⚠️ Tanpa `kind` sama sekali ⇒ server memberi NOMOR ANTREAN berikutnya. Kasir yang sedang
   * sibuk tak boleh dipaksa mengarang tanda.
   */
  park: (orderId: string, body: { kind?: 'table' | 'name' | 'queue'; label?: string }) =>
    req<{ kind: string; label: string }>(`/orders/${orderId}/park`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  /**
   * ⭐⭐ Penanda + jumlah tamu, **tanpa menahan** — dipakai saat tamunya sedang berdiri di depan
   * kasir dan yang kurang cuma namanya.
   */
  tagOrder: (
    orderId: string,
    body: { kind: 'table' | 'name' | 'queue'; label?: string; guestCount: number },
  ) =>
    req<{ kind: string; label: string; guestCount: number }>(`/orders/${orderId}/tag`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  /** ⛔ Memanggil kembali MENGAMBIL kunci sesi-sunting: dua kasir tak bisa memegang bill yang sama. */
  resume: (orderId: string) =>
    req<unknown>(`/orders/${orderId}/resume`, { method: 'POST' }),

  /** ⛔ Dipakai saat memanggil kembali: bill yang tampil kosong akan diisi ulang oleh kasir. */
  orderLines: (orderId: string) =>
    req<readonly {
      lineId: string; code: string; name: string; qty: string; sentQty: string;
      unitPrice: string; modifierLabels: string[];
    }[]>(`/orders/${orderId}/lines`),

  /** ⛔ Membuang baris DARI SERVER — menghapus di layar saja berarti item itu tetap tertagih. */
  removeLine: (orderId: string, lineId: string) =>
    req<{ removed: boolean }>(`/orders/${orderId}/lines/${lineId}/remove`, { method: 'POST' }),

  parked: (outletCode: string, businessDate: string) =>
    req<readonly ParkedOrder[]>(
      `/outlets/${outletCode}/orders-parked?businessDate=${encodeURIComponent(businessDate)}`,
    ),

  /**
   * ⭐ **Kirim ke dapur** — baris `draft` menjadi `sent` dalam satu ronde (`send_round`).
   *
   * ⛔ Ini BATAS rezim kontrol: sesudah terkirim, membatalkan baris bukan lagi menyunting layar,
   * melainkan peristiwa ber-alasan. Makanannya mungkin sudah di wajan.
   */
  sendOrder: (orderId: string, body: { actor: unknown }) =>
    req<{
      sendRound: number; lineCount: number;
      /** ⭐ Produk tanpa tujuan dapur — DILAPORKAN, tak lagi menolak pengiriman (2026-08-23). */
      unrouted?: readonly string[];
    }>(`/orders/${orderId}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * ⭐ Membatalkan seluruh bill. ⛔ `reasonCode` WAJIB dari enum kontrak — di VTEC alasan berupa
   * teks bebas berakhir 18% kosong, dan kolom yang terisi pun tak bisa dijawab saat ditanya.
   */
  /**
   * ⭐⭐⭐ **Batalkan TAGIHAN — bukan pesanannya.** Status mundur ke `sent`, barisnya utuh.
   * ⛔ Server MENOLAK bila sudah ada tender (`ORDER-409-TENDER_EXISTS`): uang yang tercatat tak
   * bisa dibatalkan diam-diam.
   */
  cancelBill: (orderId: string) =>
    req<{ orderId: string; status: string }>(`/orders/${orderId}/cancel-bill`, { method: 'POST' }),

  voidOrder: (orderId: string, body: { reasonCode: string; reasonNote?: string }) =>
    req<{ priorityReview: boolean }>(`/orders/${orderId}/void`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * ⭐ Menerapkan promo. ⛔ Server menuntut izin `order:discount` — diskon tak meninggalkan jejak
   * fisik apa pun, jadi "siapa yang memberinya" harus punya jawaban.
   */
  /**
   * ⭐⭐ **Apakah portal sudah menekan Send Data dan terminal ini belum menariknya?**
   *
   * ⚠️ Endpoint terpisah yang murah, BUKAN menumpang katalog: memanggil katalog untuk memeriksa
   * berarti tiap pemeriksaan sekaligus meng-apply — persis tarik-otomatis yang alur ini ada untuk
   * menghindarinya.
   */
  /**
   * ⭐ Daftar stok habis — murah, dipanggil berkala DAN tiap dentuman `sold_out`.
   *
   * ⛔ Tak menumpang katalog: memanggil katalog untuk memeriksa berarti tiap pemeriksaan sekaligus
   * meng-apply katalog (K25), yaitu push otomatis yang alur SYNC ada untuk menghindarinya.
   */
  soldOut: (outletCode: string) =>
    req<{ items: readonly SoldOutItem[] }>(
      `/outlets/${encodeURIComponent(outletCode)}/sold-out`,
    ),

  /**
   * ⭐⭐⭐ **Cari bill.** ⛔ Bergerbang `order:create` di server, bukan `report:view`: mencetak
   * ulang struk untuk tamu yang kembali adalah pekerjaan kasir sehari-hari. Yang bergerbang
   * `report:view` adalah AGREGAT — X/Z dan `/reports/*`.
   */
  /**
   * ⭐⭐ **Penjualan hari ini** — "apa yang laku". ⛔ Rute POS tersendiri (`pos:operate` + izin staf
   * `report:view`), bukan `/reports/*` yang bergerbang scope KONSOL: membuka scope itu untuk
   * perangkat berarti tiap tablet bisa menyisir laporan seluruh tenant.
   */
  /**
   * ⭐⭐⭐ **Perangkat mengadopsi printernya sendiri.** ⛔ Menulis profil DIRINYA (`sub` =
   * `device:<code>`), bukan perangkat mana pun — sumbu yang sama dengan `/devices/me` (K21).
   * ⚠️ `null` ⇒ profil printernya dibuang.
   */
  setOwnPrinter: (printer: Record<string, unknown> | null) =>
    req<{ deviceCode: string; printer: Record<string, unknown> | null }>('/devices/me/printer', {
      method: 'POST',
      body: JSON.stringify({ printer }),
    }),

  salesToday: (outletCode: string, businessDate?: string) =>
    req<SalesToday>(
      `/outlets/${encodeURIComponent(outletCode)}/sales-today${
        businessDate === undefined ? '' : `?businessDate=${encodeURIComponent(businessDate)}`}`,
    ),

  searchOrders: (outletCode: string, opts: {
    from?: string; to?: string; q?: string; all?: boolean;
  } = {}) => {
    const p = new URLSearchParams();
    if (opts.from !== undefined) p.set('from', opts.from);
    if (opts.to !== undefined) p.set('to', opts.to);
    if (opts.q !== undefined && opts.q !== '') p.set('q', opts.q);
    if (opts.all === true) p.set('all', 'true');
    const qs = p.toString();
    return req<{ rows: readonly FoundOrder[]; truncated: boolean }>(
      `/outlets/${encodeURIComponent(outletCode)}/orders/search${qs === '' ? '' : `?${qs}`}`,
    );
  },

  refundable: (orderId: string) =>
    req<RefundableOrder>(`/orders/${orderId}/refundable`),

  /**
   * ⭐⭐⭐ Menerbitkan refund. ⛔ Klien cuma menyebut **baris mana, berapa banyak** — gross,
   * potongan, service charge, dan pajaknya dihitung SERVER. Klien yang boleh menentukan besarnya
   * refund adalah klien yang boleh menentukan pendapatan.
   */
  refund: (orderId: string, body: {
    reasonCode: string; reasonNote?: string;
    lines: readonly { orderLineId: string; qty: string }[];
    tenders?: readonly { paymentMethodCode: string; amount?: string; referenceNo?: string }[];
    shiftId?: string;
  }) =>
    req<{
      refundId: string; refundNo: string; grandTotal: string; subtotalGross: string;
      discountTotal: string; serviceChargeTotal: string; taxTotal: string; businessDate: string;
    }>(`/orders/${orderId}/refund`, { method: 'POST', body: JSON.stringify(body) }),

  setSoldOut: (outletCode: string, productCode: string, soldOut: boolean) =>
    req<{ productCode: string; soldOut: boolean; changed: boolean }>(
      `/outlets/${encodeURIComponent(outletCode)}/sold-out`,
      { method: 'POST', body: JSON.stringify({ productCode, soldOut }) },
    ),

  catalogStatus: (outletCode: string) =>
    req<{
      outletCode: string;
      publishedAt: string | null;
      publishedBy: string | null;
      appliedAt: string | null;
      appliedVersion: string | null;
      stale: boolean;
    }>(`/outlets/${encodeURIComponent(outletCode)}/catalog/status`),

  applyPromo: (
    orderId: string,
    promoCode: string,
    /**
     * ⭐⭐ Persetujuan penyelia untuk promo bertanda `requires_approval`. ⛔ PIN **tak pernah
     * disimpan** di layar — ia hidup selama satu permintaan dan dibuang begitu jawabannya datang.
     */
    approval?: { staffCode: string; pin: string },
  ) =>
    req<{ promoCode: string; discountTotal: string; lineCount: number; approvedBy: string | null }>(
      `/orders/${orderId}/promo`,
      { method: 'POST', body: JSON.stringify({ promoCode, ...(approval === undefined ? {} : { approval }) }) },
    ),

  close: (orderId: string, body: unknown) =>
    req<{ status: string }>(`/orders/${orderId}/close`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

/**
 * ⭐⭐⭐ **Satu kalimat yang bisa DITINDAKLANJUTI kasir** (FB-TOLAK-01, UAT 2026-08-29).
 *
 * Menyusun tiga hal yang dituntut soal, dari sumber yang berbeda-beda:
 *  1. **apa yang terjadi** — `message` dari server; ia yang menyebut ANGKA & NAMA (produk mana,
 *     till mana, kurang berapa), hal yang tak mungkin ada di katalog;
 *  2. **apa yang bisa dilakukan** — `remediation` dari KATALOG galat, bukan diketik ulang tiap
 *     layar. Sampai hari ini katalog itu ada dan tak satu katanya pernah sampai ke pengguna;
 *  3. **kode kecil sebagai rujukan** — supaya penyelia/dukungan punya pegangan.
 *
 * ⛔ `trace_id` SENGAJA tidak ikut: UUID di depan tamu adalah kebisingan, dan ia sudah ada di log
 * server untuk yang benar-benar menelusuri.
 */
export function pesanGalat(e: unknown): string {
  if (e instanceof ApiFailure) {
    const d = e.detail;
    const jalanKeluar = d.remediation === undefined ? '' : ` ${d.remediation}`;
    return `${d.message}${jalanKeluar} (${d.error_code})`;
  }
  return e instanceof Error ? e.message : String(e);
}
